import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mui/material';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import {
  ColorDots,
  ColorDotsLeft,
  ColorDotsRight,
  FieldCheckLabel,
  FieldHeader,
  FieldRoot,
  FileAttachmentContent,
  FileAttachmentRoot,
  FileUploadVariant,
  HelpTextPrimary,
  HelpTextSecondary,
  HiddenInput,
  Inner,
  ProgressFill,
  ProgressTrack,
  UploadButton,
  UploadButtonWrap,
} from './styles';

export type FileUploadValidationError =
  | 'EMPTY_FILE'
  | 'OVER_MAX_SIZE'
  | 'INVALID_EXTENSION'
  | 'NAME_TOO_LONG'
  /** 단건(multiple=false)인데 한 번에 여러 개 — 전부 거절 */
  | 'TOO_MANY_FILES';

export type FileUploadRejection = {
  file: File;
  reason: FileUploadValidationError;
};

export type FileUploadProps = {
  variant?: FileUploadVariant;
  multiple?: boolean;
  /** input accept 속성 (확장자/MIME 콤마 구분) */
  accept?: string;
  /** 검증용 허용 확장자(소문자, 점 제외). 미지정이면 검증 스킵 */
  allowedExtensions?: string[];
  /** bytes 단위. 기본 500MB */
  maxSize?: number;
  disabled?: boolean;
  /** 헤더 라벨. 미지정이면 '첨부파일'. 비표시는 false. */
  label?: ReactNode | false;
  /** 헤더 우측 슬롯. 기본 '첨부 이미지 문서 하단에 표시' 체크박스. 비표시는 false. */
  headerRight?: ReactNode | false;
  /** 안내문 1행 — override 시 ReactNode */
  helpText?: ReactNode;
  /** 안내문 2행 — override 시 ReactNode */
  helpSubText?: ReactNode;
  /** 업로드 버튼 라벨. 미지정이면 '파일 업로드' */
  buttonLabel?: ReactNode;
  /** 라벨 옆 추가 마크 (AI 배지 등) */
  aiBadge?: ReactNode;
  /** 검증 통과 파일들. 실제 업로드는 상위에서 트리거. */
  onFilesAdded?: (files: File[]) => void;
  /** 검증 실패 모음 */
  onError?: (rejections: FileUploadRejection[]) => void;
  /** 진행 표시. 0~100. 미지정이면 숨김 */
  progress?: number;
  /** 박스 안 안내문 위 슬롯 (AI 태그 등) */
  topSlot?: ReactNode;
  /** 박스 전체를 덮는 슬롯 (분석 중 오버레이 등) — 표시 중엔 클릭·드롭 무시 */
  overlay?: ReactNode;
  /** 박스 아래 슬롯 */
  footer?: ReactNode;
};

const DEFAULT_MAX_SIZE = 500 * 1024 * 1024;

const getExt = (name: string) => {
  const idx = name.lastIndexOf('.');
  if (idx < 0) return '';
  return name.slice(idx + 1).toLowerCase();
};

const validateFile = (
  file: File,
  maxSize: number,
  allowedExtensions?: string[],
): FileUploadValidationError | null => {
  if (file.size === 0) return 'EMPTY_FILE';
  if (file.size > maxSize) return 'OVER_MAX_SIZE';
  if (file.name.length > 200) return 'NAME_TOO_LONG';
  if (allowedExtensions && allowedExtensions.length > 0) {
    const ext = getExt(file.name);
    if (!allowedExtensions.includes(ext)) return 'INVALID_EXTENSION';
  }
  return null;
};

const FileUpload = ({
  variant = 'default',
  multiple = true,
  accept,
  allowedExtensions,
  maxSize = DEFAULT_MAX_SIZE,
  disabled = false,
  label,
  headerRight,
  helpText,
  helpSubText,
  buttonLabel,
  aiBadge,
  onFilesAdded,
  onError,
  progress,
  topSlot,
  overlay,
  footer,
}: FileUploadProps) => {
  const { t } = useTranslation();
  // 기본 문구는 t() 가 필요해 파라미터 기본값으로 둘 수 없음 — 훅 이후에 해석.
  // label 은 false(비표시)가 유효값이라 undefined 만 기본값으로 대체.
  const resolvedLabel = label === undefined ? t('첨부파일') : label;
  const resolvedButtonLabel = buttonLabel ?? t('파일 업로드');
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const blocked = disabled || Boolean(overlay);

  const buttonIconSrc =
    variant === 'default'
      ? '/icons/approval/file-upload.svg'
      : '/icons/approval/ai-file-upload.svg';

  const processFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const incoming = Array.from(fileList);
      if (!multiple && incoming.length > 1) {
        onError?.(incoming.map((file) => ({ file, reason: 'TOO_MANY_FILES' })));
        return;
      }
      const accepted: File[] = [];
      const rejected: FileUploadRejection[] = [];
      for (const file of incoming) {
        const reason = validateFile(file, maxSize, allowedExtensions);
        if (reason) {
          rejected.push({ file, reason });
        } else {
          accepted.push(file);
        }
      }
      if (rejected.length > 0) {
        onError?.(rejected);
      }
      if (accepted.length > 0) {
        const final = multiple ? accepted : accepted.slice(0, 1);
        onFilesAdded?.(final);
      }
    },
    [allowedExtensions, maxSize, multiple, onError, onFilesAdded],
  );

  const handleClick = useCallback(() => {
    if (blocked) return;
    inputRef.current?.click();
  }, [blocked]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (blocked) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        inputRef.current?.click();
      }
    },
    [blocked],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      processFiles(e.target.files);
      e.target.value = '';
    },
    [processFiles],
  );

  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (blocked) return;
      e.preventDefault();
      e.stopPropagation();
      setActive(true);
    },
    [blocked],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      // 막힌 상태에서도 기본 동작(브라우저가 파일을 열어 페이지 이탈)은 차단
      e.preventDefault();
      e.stopPropagation();
      if (blocked) return;
      if (!active) setActive(true);
    },
    [active, blocked],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (blocked) return;
      if (e.currentTarget.contains(e.relatedTarget as Node | null) === false) {
        setActive(false);
      }
    },
    [blocked],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (blocked) return;
      setActive(false);
      processFiles(e.dataTransfer.files);
    },
    [blocked, processFiles],
  );

  const renderHeader = () => {
    if (resolvedLabel === false && headerRight === false) return null;
    return (
      <FieldHeader>
        {resolvedLabel !== false && (
          <Body1 component="span">
            {resolvedLabel}
            {aiBadge}
          </Body1>
        )}
        {headerRight === false
          ? null
          : headerRight ?? (
              <FieldCheckLabel
                control={<Checkbox size="small" disableRipple />}
                label={t('첨부 이미지 문서 하단에 표시')}
              />
            )}
      </FieldHeader>
    );
  };

  return (
    <FieldRoot>
      {renderHeader()}
      <FileAttachmentRoot
        variantKey={variant}
        active={active}
        disabled={disabled}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <FileAttachmentContent className="file-attachment-content">
          <ColorDots className="color-dots" variantKey={variant} />
          <Inner>
            {topSlot}
            <HelpTextPrimary>
              {helpText ??
                t(
                  '파일을 이곳에 끌어다 놓거나 아래 버튼을 눌러 추가해 주세요.',
                )}
            </HelpTextPrimary>
            <HelpTextSecondary>
              {helpSubText ?? t('파일당 최대 500MB / 파일 형식 제한 없음')}
            </HelpTextSecondary>
            <UploadButtonWrap>
              <UploadButton
                type="button"
                variantKey={variant}
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                <img src={buttonIconSrc} alt="" aria-hidden="true" />
                <span className="upload-label">{resolvedButtonLabel}</span>
              </UploadButton>
            </UploadButtonWrap>
            {typeof progress === 'number' && (
              <ProgressTrack>
                <ProgressFill variantKey={variant} progress={progress} />
              </ProgressTrack>
            )}
          </Inner>
        </FileAttachmentContent>
        {overlay}
        <ColorDotsLeft className="color-dots-left" variantKey={variant} />
        <ColorDotsRight className="color-dots-right" variantKey={variant} />
        <HiddenInput
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          onChange={handleChange}
        />
      </FileAttachmentRoot>
      {footer}
    </FieldRoot>
  );
};

export default FileUpload;
